import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Save, Plus, Trash2, Image, Loader2, ChevronDown, ChevronUp, GripVertical, Link } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminNutsProgram = () => {
  const [content, setContent] = useState({
    hero_title: 'Programa NUTS',
    hero_subtitle: 'El teu camí cap al golf',
    hero_image: '',
    main_description: '',
    steps: [
      { id: 'step_1', number: 1, title: 'Pas 1', description: 'Un monitor explicant a una parella al camp', image: '' },
      { id: 'step_2', number: 2, title: 'Pas 2', description: 'Una classe al camp de pràctiques', image: '' },
      { id: 'step_3', number: 3, title: 'Pas 3', description: 'Un jugador al camp', image: '' }
    ],
    activities_title: 'Activitats',
    activities: [],
    button_private_text: 'Reserva la teva Sessió Privada',
    button_private_link: 'https://wa.me/34661194023?text=Vull%20reservar%20una%20Sessió%20Privada%20NUTS',
    button_group_text: 'Reserva la teva plaça a una Sessió en Grup',
    button_group_link: 'https://wa.me/34661194023?text=Vull%20reservar%20una%20Sessió%20en%20Grup%20NUTS'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [language, setLanguage] = useState('ca');
  const [expandedSections, setExpandedSections] = useState({
    hero: true,
    description: true,
    steps: true,
    activities: true,
    buttons: true
  });

  useEffect(() => {
    fetchContent();
  }, [language]);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/nuts-program/${language}`, {
        
      });
      if (response.ok) {
        const data = await response.json();
        setContent(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error fetching NUTS content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/nuts-program/${language}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify(content)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Contingut desat correctament!' });
      } else {
        setMessage({ type: 'error', text: 'Error desant el contingut' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de connexió' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const addStep = () => {
    const newNumber = (content.steps?.length || 0) + 1;
    setContent(prev => ({
      ...prev,
      steps: [
        ...(prev.steps || []),
        {
          id: `step_${Date.now()}`,
          number: newNumber,
          title: `Pas ${newNumber}`,
          description: '',
          image: ''
        }
      ]
    }));
  };

  const updateStep = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => 
        i === index ? { ...step, [field]: value } : step
      )
    }));
  };

  const removeStep = (index) => {
    setContent(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index).map((step, i) => ({
        ...step,
        number: i + 1
      }))
    }));
  };

  // Activity management functions
  const addActivity = () => {
    setContent(prev => ({
      ...prev,
      activities: [
        ...(prev.activities || []),
        {
          id: `activity_${Date.now()}`,
          image: '',
          description: '',
          link: ''
        }
      ]
    }));
  };

  const updateActivity = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      activities: (prev.activities || []).map((activity, i) => 
        i === index ? { ...activity, [field]: value } : activity
      )
    }));
  };

  const removeActivity = (index) => {
    setContent(prev => ({
      ...prev,
      activities: (prev.activities || []).filter((_, i) => i !== index)
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto" data-testid="admin-nuts-program">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Programa NUTS</h1>
            <p className="text-gray-600 text-sm mt-1">Gestiona el contingut del programa d'iniciació al golf</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-[#1A4D2E] text-white px-6 py-2.5 rounded-lg hover:bg-[#153d24] transition-colors disabled:opacity-50"
            data-testid="save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Desar
          </button>
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {message.text}
          </div>
        )}

        {/* Language Selector */}
        <div className="mb-6 flex gap-2">
          {['ca', 'es', 'fr', 'en'].map((lang) => (
            <button
              key={lang}
              onClick={() => setLanguage(lang)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                language === lang
                  ? 'bg-[#1A4D2E] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid={`lang-${lang}`}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Hero Section */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6 overflow-hidden">
          <button
            onClick={() => toggleSection('hero')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <h2 className="font-semibold text-gray-900">Hero</h2>
            {expandedSections.hero ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          {expandedSections.hero && (
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Títol Hero</label>
                <input
                  type="text"
                  value={content.hero_title || ''}
                  onChange={(e) => setContent(prev => ({ ...prev, hero_title: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                  data-testid="hero-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subtítol Hero</label>
                <input
                  type="text"
                  value={content.hero_subtitle || ''}
                  onChange={(e) => setContent(prev => ({ ...prev, hero_subtitle: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Imatge Hero (URL)</label>
                <input
                  type="text"
                  value={content.hero_image || ''}
                  onChange={(e) => setContent(prev => ({ ...prev, hero_image: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                  placeholder="https://..."
                />
              </div>
            </div>
          )}
        </div>

        {/* Main Description */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6 overflow-hidden">
          <button
            onClick={() => toggleSection('description')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <h2 className="font-semibold text-gray-900">Descripció Principal (HTML)</h2>
            {expandedSections.description ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          {expandedSections.description && (
            <div className="p-6">
              <textarea
                value={content.main_description || ''}
                onChange={(e) => setContent(prev => ({ ...prev, main_description: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent font-mono text-sm"
                rows={10}
                placeholder="<h2>Títol</h2><p>Contingut...</p>"
                data-testid="main-description-input"
              />
            </div>
          )}
        </div>

        {/* Steps Section */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6 overflow-hidden">
          <button
            onClick={() => toggleSection('steps')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <h2 className="font-semibold text-gray-900">Passos del Programa (amb imatges)</h2>
            {expandedSections.steps ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          {expandedSections.steps && (
            <div className="p-6 space-y-6">
              {content.steps?.map((step, index) => (
                <div key={step.id || index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-gray-400" />
                      <span className="w-8 h-8 rounded-full bg-[#1A4D2E] text-white text-sm font-bold flex items-center justify-center">
                        {step.number}
                      </span>
                      <h4 className="font-medium text-gray-900">Pas {step.number}</h4>
                    </div>
                    <button
                      onClick={() => removeStep(index)}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Eliminar pas"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Títol</label>
                      <input
                        type="text"
                        value={step.title || ''}
                        onChange={(e) => updateStep(index, 'title', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                        data-testid={`step-${step.number}-title`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Imatge (URL)</label>
                      <input
                        type="text"
                        value={step.image || ''}
                        onChange={(e) => updateStep(index, 'image', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                        placeholder="https://..."
                        data-testid={`step-${step.number}-image`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Descripció</label>
                      <textarea
                        value={step.description || ''}
                        onChange={(e) => updateStep(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                        rows={2}
                        data-testid={`step-${step.number}-description`}
                      />
                    </div>
                  </div>
                  
                  {/* Image Preview */}
                  {step.image && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Vista prèvia</label>
                      <div className="w-40 h-30 rounded-lg overflow-hidden bg-gray-200">
                        <img
                          src={step.image}
                          alt={step.title || `Pas ${step.number}`}
                          className="w-full h-full object-cover"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              <button
                onClick={addStep}
                className="flex items-center gap-2 text-[#1A4D2E] hover:text-[#153d24] font-medium"
                data-testid="add-step-btn"
              >
                <Plus className="w-4 h-4" />
                Afegir Pas
              </button>
            </div>
          )}
        </div>

        {/* Activities Section */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6 overflow-hidden">
          <button
            onClick={() => toggleSection('activities')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <h2 className="font-semibold text-gray-900">Activitats (columna dreta)</h2>
            {expandedSections.activities ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          {expandedSections.activities && (
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Títol secció Activitats</label>
                <input
                  type="text"
                  value={content.activities_title || ''}
                  onChange={(e) => setContent(prev => ({ ...prev, activities_title: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                />
              </div>
              
              {/* Activities List */}
              <div className="space-y-4 mt-4">
                <label className="block text-sm font-medium text-gray-700">Llista d'activitats</label>
                {(content.activities || []).map((activity, index) => (
                  <div key={activity.id || index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900 text-sm">Activitat {index + 1}</h4>
                      <button
                        onClick={() => removeActivity(index)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Eliminar activitat"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          <Image className="w-3 h-3 inline mr-1" />
                          Imatge (URL de documents)
                        </label>
                        <input
                          type="text"
                          value={activity.image || ''}
                          onChange={(e) => updateActivity(index, 'image', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                          placeholder="https://..."
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Descripció</label>
                        <textarea
                          value={activity.description || ''}
                          onChange={(e) => updateActivity(index, 'description', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                          rows={2}
                          placeholder="Descripció de l'activitat..."
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          <Link className="w-3 h-3 inline mr-1" />
                          Link de la imatge (opcional)
                        </label>
                        <input
                          type="text"
                          value={activity.link || ''}
                          onChange={(e) => updateActivity(index, 'link', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                          placeholder="https://..."
                        />
                      </div>
                      
                      {/* Image Preview */}
                      {activity.image && (
                        <div className="mt-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Vista prèvia</label>
                          <div className="w-32 h-24 rounded overflow-hidden bg-gray-200">
                            <img
                              src={activity.image}
                              alt={`Activitat ${index + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                <button
                  onClick={addActivity}
                  className="flex items-center gap-2 text-[#1A4D2E] hover:text-[#153d24] font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Afegir Activitat
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Buttons Section */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6 overflow-hidden">
          <button
            onClick={() => toggleSection('buttons')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <h2 className="font-semibold text-gray-900">Botons de Reserva</h2>
            {expandedSections.buttons ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          {expandedSections.buttons && (
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Text botó Sessió Privada</label>
                  <input
                    type="text"
                    value={content.button_private_text || ''}
                    onChange={(e) => setContent(prev => ({ ...prev, button_private_text: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link Sessió Privada</label>
                  <input
                    type="text"
                    value={content.button_private_link || ''}
                    onChange={(e) => setContent(prev => ({ ...prev, button_private_link: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Text botó Sessió en Grup</label>
                  <input
                    type="text"
                    value={content.button_group_text || ''}
                    onChange={(e) => setContent(prev => ({ ...prev, button_group_text: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link Sessió en Grup</label>
                  <input
                    type="text"
                    value={content.button_group_link || ''}
                    onChange={(e) => setContent(prev => ({ ...prev, button_group_link: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminNutsProgram;
